import React, { useMemo } from "react";
import { Separator } from "@radix-ui/themes";
import { ChatSettingsV2 } from "../entities/ChatSettingsV2";
import { Message } from "../entities/Message";
import { QuackWidgetCard } from "./QuackWidgetCard";
import { Header } from "./Header";
import { Messages } from "./message/Messages";
import { Input } from "./Input";
import { StartNewChat } from "./StartNewChat";
import { QuackChatError } from "./QuackChatError";
import { QuestionsContext } from "./QuestionsContext";

function emptyCallback() {}

function emptyAsyncCallback<T = void>(): Promise<T> {
  return Promise.resolve(undefined as T);
}

interface QuackChatCompProps {
  tenant: string;
  settings: ChatSettingsV2;
  agents?: string[];
  messages?: Message[];
  showCSAT?: boolean;
  thinking?: boolean;
  csatScore?: number;
  csatComment?: string;
  chatEnded?: boolean;
  userSent?: boolean;
  isError?: boolean;
  isLoading?: boolean;
  predefinedMessage?: string;
  chatSessionId?: string;
  questions?: string[];
  getJourney?: () => any[];
  getDom?: () => string;
  sendMessage?: (message: string, file?: File) => Promise<void>;
  sendFormOptionSelected?: (formId: string, option: string) => Promise<void>;
  startNewChat?: () => Promise<void>;
  onCloseChat?: () => Promise<void>;
  onClose?: () => void;
  onCSATScore?: (score: number, comment?: string) => void;
  uploadFile?: (file: File) => Promise<any>;
  onEscalate?: () => Promise<void>;
  onEscalationEmail?: (email: string, additionalData?: any) => Promise<void>;
  onFirstEscalationEmail?: (email: string, additionalData?: any) => Promise<void>;
  userEmail?: string;
  contextArrowsHelperConfiguration?: {
    isToggleOn: boolean;
    emails: string[];
  };
  track?: (eventName: string, properties: any) => void;
}

export function QuackChatComp({
  tenant,
  settings,
  agents = [],
  messages = [],
  showCSAT,
  thinking,
  predefinedMessage = "",
  csatScore,
  csatComment,
  chatEnded = false,
  isError,
  isLoading,
  chatSessionId,
  questions = [],
  getJourney,
  getDom,
  sendMessage = emptyAsyncCallback,
  sendFormOptionSelected = emptyAsyncCallback,
  startNewChat = emptyAsyncCallback,
  onCloseChat = emptyAsyncCallback,
  onClose,
  onCSATScore = emptyCallback,
  uploadFile = emptyAsyncCallback,
  onEscalate = emptyAsyncCallback,
  onEscalationEmail = emptyAsyncCallback,
  onFirstEscalationEmail = emptyAsyncCallback,
  userEmail,
  contextArrowsHelperConfiguration,
  track,
}: QuackChatCompProps) {
  const {
    name,
    quackAgentName,
    customSVG,
    avatarSVG,
    userAvatarSVG,
    svgRadius,
    placeholder,
    csatType,
    allowCsatComment,
    csatCommentText,
    allowUploadFileBeforeEscalation,
    showUserBackground,
  } = settings;

  const allowUploadFile = useMemo(() => {
    if (allowUploadFileBeforeEscalation) {
      return true;
    }

    const isAgentJoined =
      agents.length > 0 || messages.some(({ author }) => author === "LIVE_AGENT");

    return isAgentJoined;
  }, [tenant, agents, messages, allowUploadFileBeforeEscalation]);

  const isLastMessageIsEmail = useMemo(() => {
    return messages.length > 0 && messages[messages.length - 1].action === "request_email";
  }, [messages]);

  const hasUserSentMessage = useMemo(() => {
    const hasUserSentMessage = messages.some((message) => message.author === "USER");

    return hasUserSentMessage;
  }, [messages]);

  return (
    <QuackWidgetCard settings={settings}>
      {isError ? (
        <QuackChatError tenant={tenant} settings={settings} onClose={onClose} />
      ) : (
        <>
          <Header
            tenant={tenant}
            name={name}
            quackAgentName={quackAgentName}
            allowNewChat={settings.allowNewChat}
            allowCloseChat={settings.allowCloseChat}
            onClose={onClose}
            agents={agents}
            onStartNewChat={startNewChat}
            onCloseChat={onCloseChat}
            minimizeIcon={settings.minimizeIcon}
          />

          <Separator orientation="horizontal" size="4" />

          <Messages
            messages={messages}
            showUserBackground={showUserBackground}
            showCSAT={showCSAT}
            allowCsatComment={allowCsatComment}
            csatCommentText={csatCommentText}
            csatType={tenant === "dremio" ? "numeric_rating" : csatType}
            customSVG={customSVG}
            avatarSVG={avatarSVG}
            userAvatarSVG={userAvatarSVG}
            svgRadius={svgRadius}
            thinking={thinking}
            isLoading={isLoading}
            csatScore={csatScore}
            csatComment={csatComment}
            onCSATScore={onCSATScore}
            onEscalate={onEscalate}
            onEscalationEmail={onEscalationEmail}
            onFirstEscalationEmail={onFirstEscalationEmail}
            tenant={tenant}
            quackAgentName={quackAgentName}
            chatEnded={chatEnded}
            sendFormOptionSelected={sendFormOptionSelected}
            chatSessionId={chatSessionId}
            getJourney={getJourney}
            getDom={getDom}
            onClose={onClose}
            userEmail={userEmail}
            contextArrowsHelperConfiguration={contextArrowsHelperConfiguration}
            track={track}
          />

          {questions.length > 0 && !hasUserSentMessage && !chatEnded && (
            <QuestionsContext options={questions} onQuestionClick={sendMessage} />
          )}

          {!chatEnded && (
            <>
              <Separator orientation="horizontal" size="4" />
              {!isLastMessageIsEmail && (
                <Input
                  predefinedMessage={predefinedMessage}
                  allowUploadFile={allowUploadFile}
                  placeholder={placeholder}
                  disabled={isLoading || thinking || isLastMessageIsEmail}
                  sendMessage={sendMessage}
                  uploadFile={uploadFile}
                />
              )}
            </>
          )}

          <StartNewChat chatEnded={chatEnded} onClick={startNewChat} />
        </>
      )}
    </QuackWidgetCard>
  );
}
